if (!customElements.get('product-details-drawer')) {
  const SHOPIFY_SECTION_SELECTOR = '[id^="shopify-section-"]';
  const MAX_SECTIONS_PER_REQUEST = 5;

  // Extra marketing sections (beyond the main product-info) to surface in the drawer, in page
  // order, each identified by a marker selector unique to its own content so it can be found
  // within a full page fetch. Their own <style>/<link> tags are siblings of that marker, not
  // descendants, so the whole .shopify-section wrapper - not just the marker element - is what
  // gets cached and injected.
  const EXTRA_SECTION_SELECTORS = [
    '.product-features-grid-section',
    '.image-text-wrapper',
    "[class^='media-content']",
    '.rich-text__wrapper',
    '.cstm_focal_ponits_conn',
  ];

  // Sections in JSON templates only have a stable id ("template--123__main") once rendered -
  // it can't be predicted up front, so the first fetch for a given product resolves every
  // section's id, and every later open of that same product reuses the Section Rendering API
  // instead of re-downloading the full page. A `null` entry means "checked, this product has
  // no section matching that selector".
  const mainSectionIdCache = new Map();
  const extraSectionIdsCache = new Map();

  customElements.define(
    'product-details-drawer',
    class ProductDetailsDrawer extends ModalDialog {
      constructor() {
        super();
        this.contentContainer = this.querySelector('[data-product-details-content]');
      }

      hide() {
        this.contentContainer.innerHTML = '';
        super.hide();
      }

      async show(opener) {
        const url = opener.getAttribute('data-product-url');
        if (!url) return;

        // "See Details" is clicked from inside the still-open mattress/add-on picker drawer -
        // show a spinner and dim it while this drawer's content loads, since nothing of this
        // drawer is visible yet for a loading state of its own to appear on.
        const sourceModal = opener.closest('modal-dialog');
        this.showLoadingOverlay(sourceModal);
        this.classList.add('product-details-drawer--loading');

        try {
          let { productElement, extraElements } = await this.fetchSections(url);

          if (!productElement) {
            // Cached ids went stale (e.g. the product's template was reassigned) - start over.
            mainSectionIdCache.delete(url);
            extraSectionIdsCache.delete(url);
            ({ productElement, extraElements } = await this.fetchSections(url));
          }
          if (!productElement) return;

          this.preprocessHTML(productElement);
          this.contentContainer.innerHTML = '';
          this.contentContainer.appendChild(productElement);
          extraElements.forEach((element) => element && this.contentContainer.appendChild(element));

          super.show(opener);
        } catch (error) {
          console.error(error);
        } finally {
          this.classList.remove('product-details-drawer--loading');
          this.hideLoadingOverlay(sourceModal);
        }
      }

      showLoadingOverlay(modal) {
        const panel = modal?.querySelector('[role="dialog"]');
        if (!panel) return;

        const overlay = document.createElement('div');
        overlay.className = 'product-details-loading-overlay';
        overlay.innerHTML = '<span class="product-details-loading-overlay__spinner"></span>';
        panel.appendChild(overlay);
      }

      hideLoadingOverlay(modal) {
        modal?.querySelector('.product-details-loading-overlay')?.remove();
      }

      fetchSections(url) {
        const mainId = mainSectionIdCache.get(url);
        const extraIds = extraSectionIdsCache.get(url);

        if (mainId && extraIds) {
          return this.fetchViaSectionRendering(url, mainId, extraIds);
        }

        return this.fetchFullPage(url);
      }

      async fetchFullPage(url) {
        const response = await fetch(url);
        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

        const productElement = responseHTML.querySelector('product-info');
        if (productElement?.dataset.section) {
          mainSectionIdCache.set(url, productElement.dataset.section);
        }

        const extraElements = EXTRA_SECTION_SELECTORS.map(
          (selector) => responseHTML.querySelector(selector)?.closest(SHOPIFY_SECTION_SELECTOR) || null
        );
        extraSectionIdsCache.set(
          url,
          extraElements.map((element) => (element ? element.id.replace('shopify-section-', '') : null))
        );

        return { productElement, extraElements };
      }

      async fetchViaSectionRendering(url, mainId, extraIds) {
        const sections = await this.fetchSectionsBatched(url, [mainId, ...extraIds].filter(Boolean));

        return {
          productElement: this.parseSectionHtml(sections[mainId], 'product-info'),
          extraElements: extraIds.map((id) => (id ? this.parseSectionHtml(sections[id], SHOPIFY_SECTION_SELECTOR) : null)),
        };
      }

      // The Section Rendering API's multi-section endpoint caps out at 5 ids per request.
      async fetchSectionsBatched(url, ids) {
        const chunks = [];
        for (let i = 0; i < ids.length; i += MAX_SECTIONS_PER_REQUEST) {
          chunks.push(ids.slice(i, i + MAX_SECTIONS_PER_REQUEST));
        }

        const chunkResults = await Promise.all(
          chunks.map((chunk) => fetch(`${url}?sections=${chunk.join(',')}`).then((response) => response.json()))
        );

        return Object.assign({}, ...chunkResults);
      }

      parseSectionHtml(html, selector) {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      // The fetched section reuses the source page's section id, which would duplicate ids
      // (e.g. "MainProduct-<id>") already present on the current page once it's injected here.
      preprocessHTML(productElement) {
        this.preventDuplicatedIds(productElement);
        this.removeVariantsAndBuyButtons(productElement);
        this.removeNestedModals(productElement);
      }

      preventDuplicatedIds(productElement) {
        const sectionId = productElement.dataset.section;
        const newId = `product-details-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(sectionId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(sectionId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(sectionId, newId));
          }
        });
      }

      removeVariantsAndBuyButtons(productElement) {
        const selectors = [
          'variant-selects',
          'beds-variant-selector',
          'beds-addons',
          'product-form',
          '.cstm_variant_picker_sc_conn',
          '.cstm_quanty_and_atc_button_rapper',
          '.product-form__quantity',
          '.qty-atc-block',
          '.recommended_add_on_container',
          '.recommended_add_on_label',
          '.custom-breadcrumb',
          '.product-payment-icons'
        ];
        productElement.querySelectorAll(selectors.join(',')).forEach((element) => element.remove());
      }

      removeNestedModals(productElement) {
        productElement.querySelectorAll('pickup-availability, product-modal, modal-dialog').forEach((element) => element.remove());
      }
    }
  );
}
